import { z } from "zod";
import { calendarDay } from "./profile";
/**
 * Pain and injury history — `GET`/`PUT /me/injuries` (`D115`).
 *
 * This is the input that makes the engine's substitution rule (T33) mean
 * anything: a current shoulder pain is why a candidate exercise is dropped and
 * a different one is proposed with a reason the person can read.
 *
 * ── THE PUT REPLACES, LIKE THE EQUIPMENT LIST ───────────────────────────────
 * The body map is a screen the person edits as a whole. Same reasoning as
 * `equipment.ts`.
 */
/**
 * The 17 regions of the body map, in the order of blueprint §4.2 item 10.
 *
 * SEVENTEEN and not twelve: the paired joints are listed left and right
 * separately (`_r` / `_l`) because *"my right knee"* is the answer people give
 * and an exercise substitution is unilateral. The order is the map's reading
 * order, top to bottom, and a screen may rely on it.
 */
export const BODY_REGIONS = [
    "cervical",
    "shoulder_r",
    "shoulder_l",
    "elbow_r",
    "elbow_l",
    "wrist_r",
    "wrist_l",
    "thoracic",
    "lumbar",
    "hip_r",
    "hip_l",
    "knee_r",
    "knee_l",
    "ankle_r",
    "ankle_l",
    "abdomen",
    "chest",
];
export const bodyRegion = z.enum(BODY_REGIONS);
/**
 * Whether it hurts NOW, or whether it happened once. The two carry different
 * follow-ups and the engine treats them differently — a current pain
 * constrains today's session, a historical injury is context.
 */
export const INJURY_OCCURRENCES = ["current", "historical"];
export const injuryOccurrence = z.enum(INJURY_OCCURRENCES);
/** Current pain: what kind, and when it shows up. */
export const INJURY_NATURES = [
    "acute",
    "chronic",
    "limitation",
    "inflammation",
];
export const INJURY_MOMENTS = ["rest", "movement", "after", "always"];
/** Historical injury: what it was, where it got to, how long ago. */
export const INJURY_TYPES = [
    "fracture",
    "ligament",
    "tendon",
    "disc",
    "surgery",
    "other",
];
export const INJURY_STATUSES = [
    "resolved",
    "resolved_limited",
    "in_treatment",
];
export const INJURY_AGES = ["lt6m", "6mto2y", "gt2y"];
/**
 * Which way a CURRENT pain is going, asked only when an existing report is
 * edited — there is nothing to compare a pain reported for the first time to.
 */
export const PAIN_TRENDS = ["improving", "same", "worsening"];
export const painTrend = z.enum(PAIN_TRENDS);
/** The longest injury note, in characters, after trimming. */
export const MAX_INJURY_NOTE_LENGTH = 280;
/**
 * One report about one region.
 *
 * ── EVERY FOLLOW-UP IS PRESENT AND NULLABLE, NEVER OPTIONAL ─────────────────
 * A flat record with both branches' fields, each explicitly `null` when it
 * does not apply, rather than a discriminated union: the body map edits a row
 * in place and a person who changes `occurrence` keeps the row. The
 * cross-field rule below is what a union would have given, stated once and
 * checked, and a `null` a client had to write is a `null` it meant.
 */
const injuryShape = {
    howLongAgo: z.enum(INJURY_AGES).nullable(),
    /** 1–10, the scale the pain step asks on. */
    intensity: z.int().min(1).max(10).nullable(),
    nature: z.enum(INJURY_NATURES).nullable(),
    occurrence: injuryOccurrence,
    region: bodyRegion,
    status: z.enum(INJURY_STATUSES).nullable(),
    type: z.enum(INJURY_TYPES).nullable(),
    when: z.enum(INJURY_MOMENTS).nullable(),
    /**
     * The pain editor's free-text "what" (`D41`), for either occurrence. It
     * takes NO part in the two refinements below — neither branch requires or
     * forbids it. Trimmed, at most 280 characters; absent, `null` and empty are
     * all "no note" (the server stores `null`). The only field here that is
     * optional rather than nullable-and-present, because every client older
     * than it sends a report without it.
     */
    note: z.string().trim().max(MAX_INJURY_NOTE_LENGTH).nullable().optional(),
    /**
     * Roughly the first day of the pain, `YYYY-MM-DD` (`calendarDay`) — CURRENT
     * reports only, and never after the server's today.
     *
     * ── A DAY, NOT THE BUCKET THE SCREEN ASKS ─────────────────────────────────
     * The editor asks it as four ranges ("this week", "this month", …) and turns
     * the answer into a date before sending, because every save RE-CREATES the
     * rows with `reportedAt = now`: a stored bucket would read as "this week"
     * forever and never age, while a date is still the same date next month.
     *
     * Absent and `null` both mean "not said". Optional rather than
     * nullable-and-present for `note`'s reason — every client older than the
     * field sends a report without it.
     *
     * ⚠️ AN OLDER APP ERASES IT, and nothing can stop that: `injuryListOutput`
     * reuses this shape, so an installed app parses the list with a plain
     * `z.object` that STRIPS the key, and the next whole-list `PUT` it sends
     * writes the pain back without it. Exactly what happened to `note` (T29).
     */
    since: calendarDay.nullable().optional(),
    /**
     * How the pain is evolving — CURRENT reports only. Absent and `null` both
     * mean "not said", which is every report made for the first time. Carries
     * `since`'s erasure warning word for word.
     */
    trend: painTrend.nullable().optional(),
};
const CURRENT_FIELDS = ["nature", "intensity", "when"];
const HISTORICAL_FIELDS = ["type", "status", "howLongAgo"];
export const injuryInput = z
    .strictObject(injuryShape)
    .refine((value) => value.occurrence === "current"
    ? CURRENT_FIELDS.every((key) => value[key] !== null)
    : HISTORICAL_FIELDS.every((key) => value[key] !== null), {
    error: "a current report needs nature, intensity and when; a historical one needs type, status and howLongAgo",
})
    .refine((value) => value.occurrence === "current"
    ? HISTORICAL_FIELDS.every((key) => value[key] === null)
    : CURRENT_FIELDS.every((key) => value[key] === null), {
    // The other branch's fields left populated is the shape a screen
    // produces when it switches `occurrence` and forgets to clear — stored,
    // it reads as a historical fracture that also hurts at rest.
    error: "the other occurrence's fields must be null",
})
    .refine((value) => value.occurrence === "current" ||
    ((value.since ?? null) === null && (value.trend ?? null) === null), {
    // Both describe a pain that is happening: a historical injury with a
    // `trend` of "improving" is a report about a thing that ended, which the
    // engine would read as a live constraint.
    error: "a historical report carries neither since nor trend",
});
/**
 * The whole list, replacing what is stored.
 *
 * The cap is 34 — every one of the 17 regions reported both as a current pain
 * and as a historical injury. It is a bound on a body, not a policy.
 */
export const MAX_INJURY_REPORTS = BODY_REGIONS.length * 2;
const injuryListShape = {
    injuries: z.array(injuryInput).max(MAX_INJURY_REPORTS),
};
export const injuryListInput = z.strictObject(injuryListShape);
export const injuryListOutput = z.object(injuryListShape);
//# sourceMappingURL=injuries.js.map